/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
// Localization
import {IntlProvider} from 'react-intl';
import {useHistory, useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';

import {SkipNavContent, SkipNavLink} from '@chakra-ui/skip-nav';
import {useQuery} from '@tanstack/react-query';

import {useAccessToken, useCategory, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react';
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components';
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import AboveHeader from '@amblique/king-living/app/components/_app/partials/above-header';
import Footer from '@salesforce/retail-react-app/app/components/footer';
// Local Project Components
import Header from '@salesforce/retail-react-app/app/components/header';
import {ListMenuContent} from '@salesforce/retail-react-app/app/components/list-menu';
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner';
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary';
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive';
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top';
import Seo from '@salesforce/retail-react-app/app/components/seo';
// Chakra
import {
  Box, Center, Fade, Spinner, useDisclosure, useStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import StoreLocatorModal from '@salesforce/retail-react-app/app/components/store-locator-modal';
import CartFlyout from '@amblique/king-living/app/components/cart-flyout';
import SearchModal from '@amblique/king-living/app/components/search-modal';
import {ShowroomSelectionPopup} from '@amblique/king-living/app/components/showroom';
import {useShowroomSync} from '@amblique/king-living/app/hooks/use-showroom-sync';
import {useLocaleSync} from '@amblique/king-living/app/hooks/use-locale-sync';

// HOCs
import {
  withCommerceSdkReact,
} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react';
import {
  ACTIVE_DATA_ENABLED, DEFAULT_LOCALE, DEFAULT_SITE_TITLE,
  HOME_HREF, THEME_COLOR,
} from '@salesforce/retail-react-app/app/constants';
// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts';
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data';
import {AddToCartModalProvider} from '@salesforce/retail-react-app/app/hooks/use-add-to-cart-modal';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
// Hooks
import {AuthModal, useAuthModal} from '@salesforce/retail-react-app/app/hooks/use-auth-modal';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useUpdateShopperContext} from '@salesforce/retail-react-app/app/hooks/use-update-shopper-context';
import CheckoutFooter from '@salesforce/retail-react-app/app/pages/checkout/partials/checkout-footer';
import CheckoutHeader from '@salesforce/retail-react-app/app/pages/checkout/partials/checkout-header';
import {fetchTranslations, getTargetLocale} from '@salesforce/retail-react-app/app/utils/locale';
import logger from '@salesforce/retail-react-app/app/utils/logger-instance';
import {getPathWithLocale} from '@salesforce/retail-react-app/app/utils/url';
// Others
import {flatten, isServer, watchOnlineStatus} from '@salesforce/retail-react-app/app/utils/utils';
// Geo-redirect
import {useGeoRedirect} from '@amblique/king-living/app/hooks/use-geo-redirect';

// AMB Components
import {MegaMenuDesktop} from '@amblique/king-living/app/components/mega-menu/desktop/mega-menu-desktop';
import {MegaMenuMobile} from '@amblique/king-living/app/components/mega-menu/mobile/mega-menu-mobile';
import {useContentfulHeader} from '@amblique/king-living/app/hooks/use-contentful-header';

// Utils
import {ensureArray, getPreferredLocaleFromCookie} from '@amblique/king-living/app/utils/utils';

const PlaceholderComponent = () => (
  <Center p="2">
    <Spinner size="lg" />
  </Center>
);

const DrawerMenuItemWithData = withCommerceSdkReact(
  ({itemComponent: ItemComponent, data, ...rest}) => (
    <Fade in={true}>
      <ItemComponent {...rest} item={data} itemComponent={DrawerMenuItemWithData} />
    </Fade>
  ),
  {
    hook: useCategory,
    queryOptions: ({item}) => ({
      parameters: {
        id: item.id,
      },
    }),
    placeholder: PlaceholderComponent,
  }
);

const ListMenuContentWithData = withCommerceSdkReact(
  ({data, ...rest}) => (
    <Fade in={true}>
      <ListMenuContent {...rest} item={data} />
    </Fade>
  ),
  {
    hook: useCategory,
    queryOptions: ({item}) => ({
      parameters: {
        id: item.id,
        levels: 2,
      },
    }),
    placeholder: PlaceholderComponent,
  }
);

const App = (props) => {
  const {children} = props;
  // const {data: categoriesTree} = useCategory({
  //   parameters: {id: CAT_MENU_DEFAULT_ROOT_CATEGORY, levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH},
  // });
  // const categories = flatten(categoriesTree || {}, 'categories');
  const {getTokenWhenReady} = useAccessToken();
  const appOrigin = useAppOrigin();
  const activeData = useActiveData();
  const history = useHistory();
  const location = useLocation();
  const authModal = useAuthModal();
  const {site, locale, buildUrl} = useMultiSite();
  const siteId = site?.id;

  // Fetch Contentful header by siteId with nested data (Header → NavigationMenu → Level 1 Menu Items)
  const {
    data: contentfulHeader,
  } = useContentfulHeader(siteId, {
    enabled: !!siteId,
    onError: (error) => {
      console.error('[DEBUG] Header Contentful - Error:', {
        error,
        message: error?.message,
        status: error?.status,
        siteId,
      });
    },
  });

  const [isOnline, setIsOnline] = useState(true);
  const styles = useStyleConfig('App');
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {
    isOpen: isOpenStoreLocator,
    onOpen: onOpenStoreLocator,
    onClose: onCloseStoreLocator,
  } = useDisclosure();

  const {
    isOpen: isOpenSearchBox,
    onOpen: onOpenSearchBox,
    onClose: onCloseSearchBox,
  } = useDisclosure();

  const {
    isOpen: isOpenCartFlyout,
    onOpen: onOpenCartFlyout,
    onClose: onCloseCartFlyout,
  } = useDisclosure();

  const targetLocale = getTargetLocale({
    getUserPreferredLocales: () => {
      // Priority order for locale selection:
      // 1. URL path locale (e.g., /fr/home → fr-CA)
      // 2. Saved preference in cookie (persists across sessions)
      // 3. Default locale for the site
      const localesFromUrl = locale?.id ? [locale.id] : [];

      // Check cookie for saved preference (only client-side)
      const cookieLocale = !isServer ? getPreferredLocaleFromCookie(site?.id) : null;
      const localesFromCookie = cookieLocale ? [cookieLocale] : [];

      // Combine preferences in priority order
      // URL takes precedence, then cookie, then default
      return [...localesFromUrl, ...localesFromCookie, DEFAULT_LOCALE].filter(Boolean);
    },
    l10nConfig: site.l10n,
  });

  // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
  // However, if the file is missing, the App would render a 404 page.
  const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
    location?.pathname
  );

  // Fetch the translation message data using the target locale.
  const {data: messages} = useQuery({
    queryKey: ['app', 'translations', 'messages', targetLocale],
    queryFn: () => {
      if (is404ForMissingTranslationFile) {
        // Return early to prevent an infinite loop
        // Otherwise, it'll continue to fetch the missing translation file again
        return {};
      }

      return fetchTranslations(targetLocale, appOrigin);
    },
    enabled: isServer,
  });

  // Used to conditionally render header/footer for checkout page
  const isCheckout = /\/checkout$/.test(location?.pathname);
  const isLogin = /\/login$/.test(location?.pathname);
  const isRegister = /\/registration$/.test(location?.pathname);
  const isResetPassword = /\/reset-password$/.test(location?.pathname);
  const isPasswordlessLogin = /\/passwordless-login-landing$/.test(location?.pathname);
  const isAuth = isLogin || isRegister || isResetPassword || isPasswordlessLogin;

  // Client-side geo-redirect (only runs in browser, not during SSR)
  useGeoRedirect({
    enabled: !isServer && !isCheckout && !isAuth,
    debug: process.env.NODE_ENV === 'development',
  });

  // Client-side locale preference redirect
  // Only redirects on FIRST visit when user has a saved preference
  // Uses sessionStorage to ensure redirect only happens once per session
  useEffect(() => {
    if (isServer || isCheckout || isAuth) return;

    // Check if we've already processed locale redirect this session
    const processedKey = `locale_processed_${site?.id}`;

    if (sessionStorage.getItem(processedKey)) return;

    // Mark as processed immediately to prevent any loops
    sessionStorage.setItem(processedKey, 'true');

    const cookieLocale = getPreferredLocaleFromCookie(site?.id);
    const currentLocale = locale?.id;
    const supportedLocales = site?.l10n?.supportedLocales || [];

    // Find if cookie locale matches any supported locale (by id or alias)
    const matchedLocale = supportedLocales.find(
      (l) => l.id === cookieLocale || l.alias === cookieLocale
    );

    // Check if current locale matches cookie (considering aliases)
    const currentMatchesCookie = supportedLocales.some(
      (l) => (l.id === currentLocale || l.alias === currentLocale) &&
        (l.id === cookieLocale || l.alias === cookieLocale)
    );

    // Only redirect if:
    // 1. Cookie has a valid preference
    // 2. Cookie locale is different from current locale
    if (cookieLocale && matchedLocale && !currentMatchesCookie) {
      window.location.href = getPathWithLocale(cookieLocale, buildUrl, {disallowParams: ['refine']});
    }
  }, []);

  const {l10n} = site;
  // Get the current currency to be used through out the app
  const currency = locale.preferredCurrency || l10n.defaultCurrency;

  // Handle creating a new basket if there isn't one already assigned to the current
  // customer.
  const {data: customer} = useCurrentCustomer();
  const {data: basket} = useCurrentBasket();

  // Sync showroom preference between cookie and SFCC account
  // This hook auto-syncs when user logs in or selects a showroom
  useShowroomSync();

  // Sync locale preference between cookie and SFCC account
  // This hook auto-syncs when user logs in or changes language
  useLocaleSync();

  const updateBasket = useShopperBasketsMutation('updateBasket');
  const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket');

  useEffect(() => {
    // update the basket currency if it doesn't match the current locale currency
    if (basket?.currency && basket?.currency !== currency) {
      updateBasket.mutate({
        parameters: {basketId: basket.basketId},
        body: {currency},
      });
    }
  }, [basket?.currency]);

  useEffect(() => {
    // update the basket customer email
    if (
      basket &&
      customer?.isRegistered &&
      customer?.email &&
      customer?.email !== basket?.customerInfo?.email
    ) {
      updateCustomerForBasket.mutate({
        parameters: {basketId: basket.basketId},
        body: {
          email: customer.email,
        },
      });
    }
  }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email]);

  useEffect(() => {
    // Listen for online status changes.
    watchOnlineStatus((isOnline) => {
      setIsOnline(isOnline);
    });
  }, []);

  // Handle updating the shopper context
  useUpdateShopperContext();

  useEffect(() => {
    // Lets automatically close the mobile navigation when the
    // location path is changed.
    onClose();
  }, [location]);

  const onLogoClick = () => {
    // Goto the home page.
    const path = buildUrl(HOME_HREF);

    history.push(path);

    // Close the drawer.
    onClose();
  };

  const onCartClick = () => {
    // Always open cart flyout
    onOpenCartFlyout();
    onClose();
  };

  const onAccountClick = () => {
    // Link to account page if registered; Header component will show auth modal for guest users
    const path = buildUrl('/account');

    history.push(path);
  };

  const onWishlistClick = () => {
    // Link to wishlist page if registered; Header component will show auth modal for guest users
    const path = buildUrl('/account/wishlist');

    history.push(path);
  };

  const onQuoteClick = () => {
    // TODO: Implement quote functionality
    // Link to quote page if registered; Header component will show auth modal for guest users
    console.log('onQuoteClick');
  };

  const trackPage = () => {
    activeData.trackPage(site.id, locale.id, currency);
  };

  useEffect(() => {
    trackPage();
  }, [location]);

  const navigationMenu = contentfulHeader?.mainNavigation;
  const menuItems = ensureArray(navigationMenu?.menuItemsCollection?.items);

  return (
    <Box className="sf-app" {...styles.container}>
      <StorefrontPreview getToken={getTokenWhenReady}>
        <Helmet>
          {ACTIVE_DATA_ENABLED && (
            <script
              id="headActiveData"
              src={getAssetUrl('static/head-active_data.js')}
              type="text/javascript"
            >
            </script>
          )}
        </Helmet>
        <IntlProvider
          defaultLocale={DEFAULT_LOCALE}
          locale={targetLocale}
          messages={messages}
          // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
          // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
          // - "extract-default-translations"
          // - "compile-translations:pseudo"
          onError={(err) => {
            if (!messages) {
              // During the ssr prepass phase the messages object has not loaded, so we can suppress
              // errors during this time.
              return;
            }
            if (err.code === 'MISSING_TRANSLATION') {
              // NOTE: Remove the console error for missing translations during development,
              // as we knew translations would be added later.
              logger.warn('Missing translation', {
                namespace: 'App.IntlProvider',
                additionalProperties: {
                  errorMessage: err.message,
                },
              });

              return;
            }
            throw err;
          }}
        >
          <CurrencyProvider currency={currency}>
            <Seo>
              <meta content={THEME_COLOR} name="theme-color" />
              <meta content={DEFAULT_SITE_TITLE} name="apple-mobile-web-app-title" />
              <link
                href={getAssetUrl('static/img/global/apple-touch-icon.png')}
                rel="apple-touch-icon"
              />
              <link href={getAssetUrl('static/manifest.json')} rel="manifest" />

              {/* Urls for all localized versions of this page (including current page)
                            For more details on hrefLang, see https://developers.google.com/search/docs/advanced/crawling/localized-versions */}
              {site.l10n?.supportedLocales.map((locale) => (
                <link
                  key={locale.id}
                  hrefLang={locale.id.toLowerCase()}
                  rel="alternate"
                  href={`${appOrigin}${getPathWithLocale(locale.id, buildUrl, {
                    location: {
                      ...location,
                      search: '',
                    },
                  })}`}
                />
              ))}
              {/* A general locale as fallback. For example: "en" if default locale is "en-GB" */}
              <link
                hrefLang={site.l10n.defaultLocale.slice(0, 2)}
                rel="alternate"
                href={`${appOrigin}${getPathWithLocale(locale.id, buildUrl, {
                  location: {
                    ...location,
                    search: '',
                  },
                })}`}
              />
              {/* A wider fallback for user locales that the app does not support */}
              <link href={`${appOrigin}/`} hrefLang="x-default" rel="alternate" />
            </Seo>

            <ScrollToTop />

            <Box display="flex" flex={1} flexDirection="column" id="app">
              <SkipNavLink zIndex="skipLink">Skip to Content</SkipNavLink>
              <StoreLocatorModal
                isOpen={isOpenStoreLocator}
                onClose={onCloseStoreLocator}
              />

              <CartFlyout
                isOpen={isOpenCartFlyout && location.pathname !== '/cart'}
                onClose={onCloseCartFlyout}
              />

              <Box {...styles.headerWrapper} zIndex={isOpenSearchBox ? 1500 : 'sticky'} data-header-wrapper>
                {!isCheckout && !isAuth ? (
                  <>
                    <AboveHeader />
                    <Header
                      onLogoClick={onLogoClick}
                      onMenuClick={onOpen}
                      onMyAccountClick={onAccountClick}
                      onMyCartClick={onCartClick}
                      onSearchBoxClick={onOpenSearchBox}
                      onStoreLocatorClick={onOpenStoreLocator}
                      onWishlistClick={onWishlistClick}
                    >
                      <HideOnDesktop>
                        <MegaMenuMobile
                          isOpen={isOpen}
                          menuItems={menuItems}
                          navigationMenu={navigationMenu}
                          onClose={onClose}
                          onLogoClick={onLogoClick}
                          onMyAccountClick={onAccountClick}
                          onMyQuoteClick={onQuoteClick}
                        />
                        {/* <DrawerMenu
                          isOpen={isOpen}
                          itemComponent={DrawerMenuItemWithData}
                          itemsCountKey="onlineSubCategoriesCount"
                          itemsKey="categories"
                          root={
                            categories?.[CAT_MENU_DEFAULT_ROOT_CATEGORY]
                          }
                          onClose={onClose}
                          onLogoClick={onLogoClick}
                        /> */}
                      </HideOnDesktop>

                      <HideOnMobile>
                        <MegaMenuDesktop menuItems={menuItems} navigationMenu={navigationMenu} />
                        {/* <ListMenu
                          contentComponent={ListMenuContentWithData}
                          itemsCountKey="onlineSubCategoriesCount"
                          itemsKey="categories"
                          root={
                            categories?.[CAT_MENU_DEFAULT_ROOT_CATEGORY]
                          }
                        /> */}
                      </HideOnMobile>
                    </Header>
                  </>
                ) : (
                  <>
                    {isCheckout ? (
                      <CheckoutHeader />
                    ) : null}
                  </>
                )}
              </Box>
              <SearchModal isOpen={isOpenSearchBox} onClose={onCloseSearchBox} />
              {!isOnline && <OfflineBanner />}
              <AddToCartModalProvider>
                <SkipNavContent
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    outline: 0,
                  }}
                >
                  <Box
                    as="main"
                    display="flex"
                    flex="1"
                    flexDirection="column"
                    id="app-main"
                    role="main"
                  >
                    <OfflineBoundary isOnline={false}>
                      {children}
                    </OfflineBoundary>
                  </Box>
                </SkipNavContent>

                {!isCheckout && !isAuth ? <Footer /> : <>{isCheckout ? <CheckoutFooter /> : null}</>}

                <AuthModal {...authModal} />
                {/* <DntNotification {...dntNotification} /> */}

                {/* Showroom Selection Popup - prompts user to select their showroom */}
                {!isCheckout && !isAuth && <ShowroomSelectionPopup />}
              </AddToCartModalProvider>
            </Box>
          </CurrencyProvider>
        </IntlProvider>
        {ACTIVE_DATA_ENABLED && (
          <script
            async="async"
            id="dwanalytics"
            src={getAssetUrl('static/dwanalytics-22.2.js')}
            type="text/javascript"
            onLoad={trackPage}
          >
          </script>
        )}
        {ACTIVE_DATA_ENABLED && (
          <script
            async="async"
            id="dwac"
            src={getAssetUrl('static/dwac-21.7.js')}
            type="text/javascript"
          >
          </script>
        )}
      </StorefrontPreview>
    </Box>
  );
};

App.propTypes = {
  children: PropTypes.node,
};

export default App;
